import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import {
    municipalities,
    getDetailedCandidatesByMunicipality,
    getStarCandidates,
} from "../data/site";

const dropdownPanel = "nav-dropdown absolute left-0 top-full z-50 w-72 border border-border bg-white shadow-lg";
const dropdownHead = "border-b border-border px-5 py-3 text-xs font-semibold uppercase tracking-widest text-teal";
const dropdownLink = "block px-5 py-2 text-sm text-navy transition-colors hover:bg-ivory hover:text-teal";

const MunicipalityDropdown = ({ m }) => {
    const detailed = getDetailedCandidatesByMunicipality(m.slug);
    return (
        <div className={dropdownPanel}>
            <div className={dropdownHead}>2026 Mayoral Candidates</div>
            <ul className="py-2">
                {detailed.map((c) => (
                    <li key={c.slug}>
                        <Link to={`/candidates/${c.slug}`} data-testid={`nav-candidate-${c.slug}`} className={dropdownLink}>
                            {c.name}
                        </Link>
                    </li>
                ))}
            </ul>
            <div className="border-t border-border px-5 py-3">
                <Link to={`/${m.slug}`} data-testid={`nav-${m.slug}-overview`} className="text-xs font-semibold uppercase tracking-widest text-navy link-underline">
                    {m.name} election overview
                </Link>
            </div>
        </div>
    );
};

const StarDropdown = () => (
    <div className={dropdownPanel}>
        <div className={dropdownHead}>Star Candidates</div>
        <ul className="py-2">
            {getStarCandidates().map((c) => (
                <li key={c.slug}>
                    <Link to={`/candidates/${c.slug}`} data-testid={`nav-star-${c.slug}`} className={dropdownLink}>
                        {c.name}
                        <span className="block text-xs text-muted-foreground">{c.office}</span>
                    </Link>
                </li>
            ))}
        </ul>
    </div>
);

export const Header = () => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [expanded, setExpanded] = useState(null);
    const location = useLocation();

    useEffect(() => {
        setMobileOpen(false);
        setExpanded(null);
    }, [location.pathname]);

    return (
        <header data-testid="site-header" className="sticky top-0 z-40 border-b border-border bg-ivory/95 backdrop-blur">
            <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6">
                <Link to="/" data-testid="wordmark-link" className="font-serif-display text-xl font-bold tracking-tight text-navy sm:text-2xl">
                    HALTONS<span className="text-gold">.</span>CA
                </Link>

                <nav data-testid="desktop-nav" aria-label="Main navigation" className="hidden items-center gap-6 lg:flex">
                    <NavLink to="/" data-testid="nav-home" className="text-sm font-medium text-navy link-underline">
                        Home
                    </NavLink>
                    {municipalities.map((m) => (
                        <div key={m.slug} className="nav-group relative">
                            <button
                                type="button"
                                data-testid={`nav-${m.slug}-button`}
                                aria-haspopup="true"
                                className="flex items-center gap-1 text-sm font-medium text-navy link-underline"
                            >
                                {m.name}
                                <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                            </button>
                            <MunicipalityDropdown m={m} />
                        </div>
                    ))}
                    <div className="nav-group relative">
                        <button
                            type="button"
                            data-testid="nav-star-candidates-button"
                            aria-haspopup="true"
                            className="flex items-center gap-1 text-sm font-medium text-navy link-underline"
                        >
                            Star Candidates
                            <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                        </button>
                        <StarDropdown />
                    </div>
                    <NavLink to="/issues" data-testid="nav-issues" className="text-sm font-medium text-navy link-underline">
                        Issues &amp; Stories
                    </NavLink>
                    <NavLink to="/about" data-testid="nav-about" className="text-sm font-medium text-navy link-underline">
                        About
                    </NavLink>
                    <NavLink to="/contact" data-testid="nav-contact" className="text-sm font-medium text-navy link-underline">
                        Contact
                    </NavLink>
                </nav>

                <button
                    type="button"
                    data-testid="mobile-menu-button"
                    aria-label={mobileOpen ? "Close menu" : "Open menu"}
                    aria-expanded={mobileOpen}
                    onClick={() => setMobileOpen((v) => !v)}
                    className="text-navy lg:hidden"
                >
                    {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
            </div>

            {mobileOpen && (
                <nav data-testid="mobile-nav" aria-label="Mobile navigation" className="border-t border-border bg-ivory lg:hidden">
                    <div className="mx-auto max-w-6xl px-4 py-4 sm:px-6">
                        <Link to="/" data-testid="mobile-nav-home" className="block py-2 text-sm font-semibold text-navy">
                            Home
                        </Link>
                        {municipalities.map((m) => (
                            <div key={m.slug} className="border-t border-border/60">
                                <button
                                    type="button"
                                    data-testid={`mobile-nav-${m.slug}-toggle`}
                                    aria-expanded={expanded === m.slug}
                                    onClick={() => setExpanded((v) => (v === m.slug ? null : m.slug))}
                                    className="flex w-full items-center justify-between py-3 text-sm font-semibold text-navy"
                                >
                                    {m.name}
                                    <ChevronDown
                                        className={`h-4 w-4 transition-transform ${expanded === m.slug ? "rotate-180" : ""}`}
                                        aria-hidden="true"
                                    />
                                </button>
                                {expanded === m.slug && (
                                    <div className="pb-3 pl-3">
                                        <Link to={`/${m.slug}`} data-testid={`mobile-nav-${m.slug}-overview`} className="block py-1.5 text-sm font-medium text-teal">
                                            {m.name} election overview
                                        </Link>
                                        {getDetailedCandidatesByMunicipality(m.slug).map((c) => (
                                            <Link
                                                key={c.slug}
                                                to={`/candidates/${c.slug}`}
                                                data-testid={`mobile-nav-candidate-${c.slug}`}
                                                className="block py-1.5 text-sm text-navy"
                                            >
                                                {c.name}
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                        <div className="border-t border-border/60">
                            <button
                                type="button"
                                data-testid="mobile-nav-star-toggle"
                                aria-expanded={expanded === "star"}
                                onClick={() => setExpanded((v) => (v === "star" ? null : "star"))}
                                className="flex w-full items-center justify-between py-3 text-sm font-semibold text-navy"
                            >
                                Star Candidates
                                <ChevronDown
                                    className={`h-4 w-4 transition-transform ${expanded === "star" ? "rotate-180" : ""}`}
                                    aria-hidden="true"
                                />
                            </button>
                            {expanded === "star" && (
                                <div className="pb-3 pl-3">
                                    {getStarCandidates().map((c) => (
                                        <Link
                                            key={c.slug}
                                            to={`/candidates/${c.slug}`}
                                            data-testid={`mobile-nav-star-${c.slug}`}
                                            className="block py-1.5 text-sm text-navy"
                                        >
                                            {c.name}
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="border-t border-border/60">
                            <Link to="/issues" data-testid="mobile-nav-issues" className="block py-3 text-sm font-semibold text-navy">
                                Issues &amp; Stories
                            </Link>
                        </div>
                        <div className="border-t border-border/60">
                            <Link to="/about" data-testid="mobile-nav-about" className="block py-3 text-sm font-semibold text-navy">
                                About
                            </Link>
                        </div>
                        <div className="border-t border-border/60">
                            <Link to="/contact" data-testid="mobile-nav-contact" className="block py-3 text-sm font-semibold text-navy">
                                Contact
                            </Link>
                        </div>
                    </div>
                </nav>
            )}
        </header>
    );
};
